import {
    Autocomplete,
    AutocompleteOption,
    AutocompleteOptionsProps,
} from "components/Form/components/Autocomplete/Autocomplete";
import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./ExtendedAutocomplete.module.scss";

interface ExtendedAutocompleteProps {
    name: string;
    value: any;
    additionalProps: any;
    options: AutocompleteOptionsProps;
    defaultValue?: AutocompleteOption;
    onChange: (value) => void;
    specialValue: AutocompleteOption;
    extendedAutocompleteOptions: AutocompleteOptionsProps;
}

export const ExtendedAutocomplete = ({
    name,
    value,
    additionalProps,
    options,
    defaultValue,
    onChange,
    specialValue,
    extendedAutocompleteOptions,
}: ExtendedAutocompleteProps) => {
    const currentValue = value || defaultValue?.value || null;

    const [mainValue, setMainValue] = useState(null);
    const [extendedValue, setExtendedValue] = useState(null);

    const hasFetchedInitialOption = useRef(false);

    const optionsListWithDefaultValue = useMemo(
        () => (defaultValue ? [...(options?.list || []), defaultValue] : options?.list || []),
        [defaultValue, options?.list]
    );

    useEffect(() => {
        if (hasFetchedInitialOption.current) return;

        if (
            additionalProps?.hasInitialValuesLoaded === undefined ||
            additionalProps?.hasInitialValuesLoaded === true
        ) {
            const specialSelectedValue = !optionsListWithDefaultValue?.find(
                (option) => option.value === currentValue
            )
                ? currentValue
                : null;
            setMainValue(specialSelectedValue ? specialValue.value : currentValue);
            setExtendedValue(specialSelectedValue);

            hasFetchedInitialOption.current = true;
        }
    }, [
        additionalProps?.hasInitialValuesLoaded,
        optionsListWithDefaultValue,
        specialValue.value,
        currentValue,
    ]);

    useEffect(() => {
        // Wait until the initial option has been resolved; otherwise this effect
        // runs once with mainValue still null and writes null into the parent form.
        if (!hasFetchedInitialOption.current || mainValue === null) {
            return;
        }
        const nextValue = mainValue === specialValue.value ? extendedValue : mainValue;
        if (nextValue !== value) {
            onChange(nextValue);
        }
    }, [extendedValue, mainValue, onChange, specialValue.value, value]);

    return (
        <div className={styles.extendedAutocompleteWrapper}>
            <Autocomplete
                name={name}
                options={options}
                defaultValue={defaultValue}
                value={mainValue}
                additionalProps={additionalProps}
                onChange={setMainValue}
            />
            {mainValue === specialValue.value && (
                <Autocomplete
                    name={`additionalInput-${name}`}
                    options={extendedAutocompleteOptions}
                    value={extendedValue}
                    additionalProps={{ ...additionalProps, clearable: true }}
                    onChange={(selectedValue) => {
                        setExtendedValue(selectedValue);
                        if (selectedValue === null) {
                            setMainValue(specialValue.value);
                        }
                    }}
                />
            )}
        </div>
    );
};
